import React from 'react';
import { useSelector } from 'react-redux';
import { useAppContext } from 'src/components/app-context';

import { OfferListFilterContextContainer } from './components/offer-list-filter-context';

import { ProductPage } from './product-page';
import FeedbackButton from '../feedback/feedback-button';
import productPageQuery from './query.graphql';

import type { AppState } from 'src/reducers';

import { PRODUCT_CONTEXT_ID } from './constants';

import type { Variables, Group } from './product-types';
import type { ProductPageData, RouteSection } from './types';
import { useProductPageQuery } from './queries/use-product-page';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import Loader from 'src/components/loader';
import { PageContent } from 'src/components/page';

type Props = {
  routeSection: RouteSection;
};

const Product = (props: Props) => {
  const route = useMatchedRoute();
  const variables = route?.variables;
  const { data, isLoading, isSuccess } = useProductPageQuery(variables);
  const { routeSection } = props;

  const { t } = useAppContext();

  const me = useSelector((state: AppState) => state.areas.account.me);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <div key={variables.id}>
      {/* The key is used since we have routes with format produkt.php?p=id.
      When switching in between these routes want to remount the page.
      Without the key React would just reuse the component without remounting
      it since we are just changing the search params. */}
      <OfferListFilterContextContainer>
        {me && me.analyticsAccess && (
          <FeedbackButton>{t('feedback', 'button.text')}</FeedbackButton>
        )}
        <PageContent withAds>
          {isLoading && <Loader />}
          {isSuccess && (
            <ProductPage
              data={data as unknown as ProductPageData}
              routeSection={routeSection}
            />
          )}
        </PageContent>
      </OfferListFilterContextContainer>
    </div>
  );
};

Product.graphQL = productPageQuery;

// Group on product ID, ignore section param
Product.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

Product.variables = ({ id, marketCode }: Variables) => {
  const expertsEnabled = ['se'].includes(marketCode);
  return {
    id: parseInt(id, 10),
    marketCode,
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    advized: expertsEnabled,
  };
};

export default Product;
